// Generieke combobox voor zoeken in Bouwflow (Renotex ERP). Twee varianten:
//   <BouwflowProjectPicker />              — kies een Bouwflow Project
//   <BouwflowSubcontractorGroupPicker />   — kies een Bouwflow ploeg
// Beide debouncen 300ms, tonen tot 25 resultaten, hebben een "wis"-knop.
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';

// === Project ===

export interface BouwflowProjectSelection {
  id: number;
  projectId: string;   // bv. "P-2026-001"
  name: string;
}

export function BouwflowProjectPicker({
  value,
  onChange,
  disabled,
  placeholder = 'Zoek Bouwflow-project op naam of nummer…',
}: {
  value: BouwflowProjectSelection | null;
  onChange: (sel: BouwflowProjectSelection | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  if (value) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary/30 rounded-md">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-accent">{value.projectId}</div>
          <div className="text-sm font-medium truncate">{value.name}</div>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-secondary hover:text-danger px-2 py-1 rounded"
            aria-label="Wis project"
          >
            ✕
          </button>
        )}
      </div>
    );
  }

  return (
    <Combobox<BouwflowProjectSelection>
      placeholder={placeholder}
      disabled={disabled}
      fetcher={async (q) => {
        try {
          const r = await api.bouwflow.projects(q);
          return r.items.map((p) => ({ id: p.id, projectId: p.projectId, name: p.name }));
        } catch (e) {
          if (e instanceof ApiError && e.status === 503) throw new Error('Bouwflow niet geconfigureerd');
          throw e;
        }
      }}
      keyOf={(p) => String(p.id)}
      renderItem={(p) => (
        <>
          <span className="text-xs font-bold text-accent w-20 shrink-0">{p.projectId}</span>
          <span className="text-sm truncate">{p.name}</span>
        </>
      )}
      onSelect={onChange}
    />
  );
}

// === Subcontractor group ===

export interface BouwflowGroupSelection {
  id: number;
  name: string;
  subcontractorName: string | null;
  localPloeg: { id: string; team: string; firma: string; bouwflowId: number | null } | null;
}

export function BouwflowSubcontractorGroupPicker({
  value,
  onChange,
  disabled,
  placeholder = 'Zoek Bouwflow-ploeg…',
}: {
  value: BouwflowGroupSelection | null;
  onChange: (sel: BouwflowGroupSelection | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  if (value) {
    const mapped = !!value.localPloeg;
    return (
      <div className={`flex items-center gap-2 px-3 py-2 border rounded-md ${mapped ? 'bg-primary-50 border-primary/30' : 'bg-warning-100 border-warning/30'}`}>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{value.name}</div>
          <div className="text-xs text-secondary truncate">
            {value.subcontractorName || '—'}
            {mapped
              ? <> · <span className="font-bold text-success">gemapt → {value.localPloeg!.team}</span></>
              : <> · <span className="font-bold text-warning">niet gemapt — koppel via Onderaannemers</span></>}
          </div>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-secondary hover:text-danger px-2 py-1 rounded"
            aria-label="Wis ploeg"
          >
            ✕
          </button>
        )}
      </div>
    );
  }

  return (
    <Combobox<BouwflowGroupSelection>
      placeholder={placeholder}
      disabled={disabled}
      fetcher={async (q) => {
        try {
          const r = await api.bouwflow.subcontractorGroups(q);
          return r.items.map((g) => ({
            id: g.id,
            name: g.name,
            subcontractorName: g.subcontractorName,
            localPloeg: g.localPloeg,
          }));
        } catch (e) {
          if (e instanceof ApiError && e.status === 503) throw new Error('Bouwflow niet geconfigureerd');
          throw e;
        }
      }}
      keyOf={(g) => String(g.id)}
      renderItem={(g) => (
        <>
          <span className="text-sm font-medium truncate flex-1">{g.name}</span>
          <span className="text-xs text-secondary truncate ml-3 max-w-[180px]">{g.subcontractorName ?? ''}</span>
          {g.localPloeg && <span className="chip-success ml-2 shrink-0">gemapt</span>}
        </>
      )}
      onSelect={onChange}
    />
  );
}

// === Generieke Combobox primitive ===

interface ComboboxProps<T> {
  placeholder: string;
  disabled?: boolean;
  fetcher: (query: string) => Promise<T[]>;
  keyOf: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
  onSelect: (item: T) => void;
}

function Combobox<T>({ placeholder, disabled, fetcher, keyOf, renderItem, onSelect }: ComboboxProps<T>) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcher(debounced)
      .then((res) => { if (!cancelled) setItems(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Fout bij ophalen'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debounced, open, fetcher]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="search"
        className="input"
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg z-30 max-h-72 overflow-auto">
          {loading ? (
            <div className="px-3 py-2 text-xs text-secondary">Bezig met zoeken…</div>
          ) : error ? (
            <div className="px-3 py-2 text-xs text-danger">{error}</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-2 text-xs text-secondary">{debounced ? 'Geen resultaten' : 'Begin te typen…'}</div>
          ) : (
            <ul className="py-1">
              {items.map((it) => (
                <li
                  key={keyOf(it)}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-primary-50"
                  onClick={() => { onSelect(it); setOpen(false); setQuery(''); }}
                >
                  {renderItem(it)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
